import { useNavigate, useParams } from "react-router";

import { Button } from "../../components/Button";
import { Chip } from "../../components/Chip";
import { Sheet } from "../../components/Dialog";
import { EmptyState } from "../../components/EmptyState";
import { SearchIcon } from "../../components/icons";
import { Skeleton } from "../../components/Skeleton";
import { StatusGlyph } from "../../components/StatusGlyph";
import { STATUSES } from "../../domain/status";
import { nl } from "../../i18n/nl";
import { DESKTOP_QUERY, useMediaQuery } from "../../lib/useMediaQuery";
import { CompanyDetailPanel } from "./CompanyDetailPanel";
import { CompanyList } from "./CompanyList";
import styles from "./companies.module.css";
import { countFor, filterLabel, toQuery, useCompanyFilters } from "./filters";
import { useCompanyList } from "./queries";

const t = nl.companies;

function ListSkeleton() {
  return (
    <div className={styles.skeletons} role="status" aria-busy="true" aria-label={nl.common.loading}>
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className={styles.skeletonRow}>
          <Skeleton width={40} height={40} radius={12} />
          <div className={styles.skeletonText}>
            <Skeleton width="60%" />
            <Skeleton width="40%" height={12} />
          </div>
        </div>
      ))}
    </div>
  );
}

export function CompaniesPage() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const desktop = useMediaQuery(DESKTOP_QUERY);
  const { filter, q, params, update } = useCompanyFilters();
  const list = useCompanyList(toQuery(filter, q));
  const firstPage = list.data?.pages[0];
  const companies = list.data?.pages.flatMap((page) => page.items) ?? [];
  const search = params.toString() ? `?${params.toString()}` : "";

  function select(id: string) {
    void navigate(`/bedrijven/${id}${search}`);
  }

  function closeDetail() {
    void navigate(`/${search}`);
  }

  let body;
  if (list.isPending) {
    body = <ListSkeleton />;
  } else if (list.isError) {
    body = (
      <EmptyState
        title={t.loadError}
        action={<Button onClick={() => void list.refetch()}>{nl.common.retry}</Button>}
      />
    );
  } else if (companies.length === 0) {
    const filtered = filter !== "alle" || q !== "";
    body = (
      <EmptyState
        title={filtered ? t.emptyFilter : t.empty}
        action={
          filtered ? (
            <Button
              onClick={() => {
                update({ filter: "alle", q: "" });
              }}
            >
              {t.clearFilters}
            </Button>
          ) : undefined
        }
      />
    );
  } else {
    body = (
      <>
        <CompanyList companies={companies} selectedId={companyId} onSelect={select} />
        {list.hasNextPage && (
          <div className={styles.more}>
            <Button disabled={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
              {list.isFetchingNextPage ? nl.common.loading : t.loadMore}
            </Button>
          </div>
        )}
      </>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.listColumn}>
        <h1 className={styles.title}>{desktop ? nl.screens.companies : t.mobileTitle}</h1>
        <label className={styles.search}>
          <SearchIcon size={16} />
          <span className="sr-only">{t.searchLabel}</span>
          <input
            type="search"
            value={q}
            placeholder={t.searchPlaceholder}
            onChange={(event) => {
              update({ q: event.target.value });
            }}
          />
        </label>
        <div className={styles.chips} role="group" aria-label={t.filters}>
          {(["voor-jou", "alle", ...STATUSES] as const).map((option) => (
            <Chip
              key={option}
              active={filter === option}
              icon={option === "alle" || option === "voor-jou" ? undefined : <StatusGlyph status={option} size={12} />}
              count={firstPage ? countFor(option, firstPage.counts) : undefined}
              onClick={() => {
                update({ filter: option });
              }}
            >
              {filterLabel(option)}
            </Chip>
          ))}
        </div>
        {firstPage && (
          <p className={styles.count} aria-live="polite">
            {t.count(firstPage.total)}
            {desktop && companies.length > 0 && <span className="sr-only"> · {t.keyboardHint}</span>}
          </p>
        )}
        {body}
      </div>

      {desktop ? (
        <div className={styles.detailColumn}>
          {companyId ? (
            <CompanyDetailPanel key={companyId} id={companyId} />
          ) : (
            <div className={styles.detail}>
              <EmptyState title={t.selectHint} />
            </div>
          )}
        </div>
      ) : (
        <Sheet
          open={companyId !== undefined}
          onOpenChange={(open) => {
            if (!open) closeDetail();
          }}
          title={companies.find((c) => c.id === companyId)?.name ?? nl.screens.companies}
        >
          {companyId && <CompanyDetailPanel id={companyId} inSheet />}
        </Sheet>
      )}
    </div>
  );
}
